'use client';

import { Spinner } from '@common/components';
import { Pagination } from '@common/components/molecule/Pagination';
import { useToast } from '@common/hooks';
import { preventSkipToken } from '@common/utils/preventSkipToken';
import { cn, CustomError, isEnumValue } from '@core/utils';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { debounce } from 'es-toolkit';
import { usePathname } from 'next/navigation';
import { Suspense, useEffect, useMemo, useState } from 'react';
import CountUp from 'react-countup';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';

import type { COMMENT_TYPE } from '@/api';
import {
  AuthInfiniteQueryOptions,
  AuthQueryOptions,
  COMMENT_SORT_TYPE,
  LIKE_TYPE,
  UnauthInfiniteQueryOptions,
  UnauthQueryOptions,
} from '@/api';
import {
  type Comment,
  type CommentData,
  type CommentListPage,
  selectCommentList,
  toContentIdQuery,
} from '@/api/getCommentList';
import { BREAKPOINTS, ROUTES, SEARCH_PARAMS, SELECTOR } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLike } from '@/hooks/useLike';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/AuthStore';
import { isBot } from '@/utils/isBot';

import { CommentCard } from './CommentCard';
import { CommentSkeletons } from './CommentSkeleton';
import { PointEmptySection } from './PointEmptySection';

interface CommentCardsProps {
  commentType: COMMENT_TYPE;
  isLogin: boolean;
  rootCommentId?: number;
  contentId: number | string;
  sortType: COMMENT_SORT_TYPE;
  initialPageData?: CommentListPage;
  onChangeSortType?: (sortType: COMMENT_SORT_TYPE) => void;
  onClickReplyButton?: (
    comment: Comment,
    rootCommentId: number,
    parentCommentId: number,
  ) => void;
  onClickEditButton?: (comment: Comment, isRoot: boolean) => void;
  onDeleteSuccess?: (deletedCommentId: number) => void;
  onClickPickButton?: (commentId: number) => void;
  onChangeCommentData?: (data: CommentData) => void;
  className?: string;
  hasPickButton?: boolean;
}

const SIZE = 20;
const INITIAL_PAGE = 1;

const replaceSortTypeInUrl = (sortType: COMMENT_SORT_TYPE) => {
  const url = new URL(window.location.href);
  url.searchParams.set(SEARCH_PARAMS.SORT_TYPE, sortType);
  window.history.replaceState(
    null,
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

interface CommentListHeaderProps {
  isLogin: boolean;
  sortType: COMMENT_SORT_TYPE;
  visibleTotalCount: number;
  onChangeSortType?: (sortType: COMMENT_SORT_TYPE) => void;
}

const CommentListHeader = ({
  isLogin,
  sortType,
  visibleTotalCount,
  onChangeSortType,
}: CommentListHeaderProps) => {
  const { toast } = useToast();
  const handleChangeSortType = onChangeSortType ?? replaceSortTypeInUrl;

  const requireLoginToast = () => {
    if (!isLogin) {
      toast({ message: '로그인 후 이용해주세요.' });

      return true;
    }

    return false;
  };

  return (
    <header className="mb-16 flex items-center justify-between">
      <strong className="font-600 text-Gray-900 typo-body4">
        {isLogin ? (
          <>
            댓글 <CountUp preserveValue duration={1} end={visibleTotalCount} />
          </>
        ) : null}
      </strong>
      <div className="flex items-center gap-8 font-500 text-Gray-500 typo-body4">
        <button
          className={cn({
            'text-Gray-800': sortType === COMMENT_SORT_TYPE.LATEST,
          })}
          type="button"
          onClick={() => {
            if (requireLoginToast()) return;
            handleChangeSortType(COMMENT_SORT_TYPE.LATEST);
          }}
        >
          최신순
        </button>
        <span className="typo-caption1">|</span>
        <button
          className={cn({
            'text-Gray-800': sortType === COMMENT_SORT_TYPE.POPULARITY,
          })}
          type="button"
          onClick={() => {
            if (requireLoginToast()) return;
            handleChangeSortType(COMMENT_SORT_TYPE.POPULARITY);
          }}
        >
          인기순
        </button>
      </div>
    </header>
  );
};

interface CommentItemsProps {
  commentList: CommentData['commentList'];
  isLogin: boolean;
  hasPickButton?: boolean;
  isInitialChildrenOpened: boolean;
  onLike: (commentId: number, isLike: boolean) => void;
  onClickReplyButton?: (
    comment: Comment,
    rootCommentId: number,
    parentCommentId: number,
  ) => void;
  onClickEditButton?: (comment: Comment, isRoot: boolean) => void;
  onDeleteSuccess?: (deletedCommentId: number) => void;
  onClickPickButton?: (commentId: number) => void;
}

const CommentItems = ({
  commentList,
  isLogin,
  hasPickButton,
  isInitialChildrenOpened,
  onLike,
  onClickReplyButton,
  onClickEditButton,
  onDeleteSuccess,
  onClickPickButton,
}: CommentItemsProps) => {
  if (commentList.length === 0) {
    return (
      <PointEmptySection
        mainMessage="아직 댓글이 없어요"
        subMessage="지금 첫 댓글을 남겨보세요!"
      />
    );
  }

  return (
    <>
      {commentList.map((comment) => {
        const { commentId } = comment;

        return (
          <li key={commentId}>
            <CommentCard
              comment={comment}
              hasMenu={isLogin}
              hasPickButton={
                !comment.isMine && comment.isWelloHelper && hasPickButton
              }
              initialChildrenOpened={isInitialChildrenOpened}
              onDeleteSuccess={onDeleteSuccess}
              onClickEditButton={() => {
                onClickEditButton?.(comment, true);
              }}
              onClickLikeButton={() => onLike(commentId, comment.isMyThumbUp)}
              onClickPickButton={() => onClickPickButton?.(commentId)}
              onClickReplyButton={() => {
                onClickReplyButton?.(comment, commentId, commentId);
              }}
            >
              {comment.childCommentList?.length ? (
                <ul className="ml-52 mt-16 flex flex-col gap-8">
                  {comment.childCommentList.map((childrenComment) => {
                    const { commentId: childrenCommentId } = childrenComment;

                    return (
                      <li key={childrenCommentId}>
                        <CommentCard
                          comment={childrenComment}
                          hasMenu={isLogin}
                          onDeleteSuccess={onDeleteSuccess}
                          onClickEditButton={() => {
                            onClickEditButton?.(childrenComment, false);
                          }}
                          onClickLikeButton={() =>
                            onLike(
                              childrenCommentId,
                              childrenComment.isMyThumbUp,
                            )
                          }
                          onClickReplyButton={() => {
                            onClickReplyButton?.(
                              childrenComment,
                              commentId,
                              childrenCommentId,
                            );
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </CommentCard>
          </li>
        );
      })}
    </>
  );
};

const LoginRequiredSection = () => {
  const router = useCustomRouter();
  const pathname = usePathname();

  return (
    <PointEmptySection
      actionButton={{
        text: '회원 가입하기',
        onClick: () =>
          router.push(
            ROUTES.LOGIN.withSearchParams({
              searchParams: { redirect: pathname ?? '' },
            }),
          ),
      }}
      mainMessage="댓글을 확인하려면? 👀"
      subMessage={`로그인 후 볼 수 있어요.\n회원가입 후 유저들의 반응을 확인해보세요!`}
    />
  );
};

const CommentCardsInfinite = ({
  isLogin,
  commentType,
  rootCommentId,
  contentId,
  sortType,
  initialPageData,
  onChangeSortType,
  onClickReplyButton,
  onClickEditButton,
  onDeleteSuccess,
  onClickPickButton,
  onChangeCommentData,
  className,
  hasPickButton,
}: CommentCardsProps) => {
  const userId = useAuthStore((state) => state.myInfo?.id);

  const commentListQuery = useInfiniteQuery({
    ...preventSkipToken(
      (isLogin
        ? AuthInfiniteQueryOptions['get:/wello/v2/auth/wello-comment']
        : UnauthInfiniteQueryOptions['get:/wello/v2/allows/wello-comment'])({
        query: {
          page: INITIAL_PAGE,
          commentTypeCd: commentType,
          commentSort: sortType,
          size: SIZE,
          parentCommentId: rootCommentId,
          ...toContentIdQuery(contentId),
        },
      }),
    ),
    enabled: !isBot(),
    initialData: initialPageData
      ? { pages: [initialPageData], pageParams: [INITIAL_PAGE] }
      : undefined,
    staleTime: Number.POSITIVE_INFINITY,
    getNextPageParam: ({ context }) => {
      const { rootTotalCount: root_total_count, page } = context ?? {};

      if (!root_total_count || !page) return;

      const nextPage = page + 1;
      const maxPage = Math.ceil(root_total_count / SIZE);

      if (maxPage < nextPage) return;

      return nextPage;
    },
    select: ({ pages }) => {
      const lastCommentPage = selectCommentList(
        pages[pages.length - 1].context,
        userId,
      );

      const flatCommentList = pages.flatMap(
        ({ context }) => selectCommentList(context, userId).commentList,
      );

      const uniqueCommentList = flatCommentList.filter(
        (comment, index, self) =>
          index === self.findIndex((t) => t.commentId === comment.commentId),
      );

      return {
        ...lastCommentPage,
        commentList: uniqueCommentList,
      };
    },
    initialPageParam: INITIAL_PAGE,
    placeholderData: keepPreviousData,
  });

  const { commentList = [], visibleTotalCount = 0 } =
    commentListQuery.data ?? {};
  const { fetchNextPage } = commentListQuery;

  const debouncedFetchNextPage = useMemo(() => {
    return debounce(fetchNextPage, 1_000);
  }, [fetchNextPage]);

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        debouncedFetchNextPage();
      }
    },
  });

  const { like } = useLike();

  const handleLike = (commentId: number, isLike: boolean) => {
    like({
      contentId: commentId,
      isLike,
      likeType: LIKE_TYPE.COMMENT,
    });
  };

  useEffect(() => {
    if (!onChangeCommentData || !commentListQuery.data) return;

    onChangeCommentData(commentListQuery.data);
  }, [commentListQuery.data, onChangeCommentData]);

  const isRootComment = !rootCommentId;
  const isInitialChildrenOpened = commentList.length < 50;

  return (
    <section
      className={cn('bg-Gray-white px-16', className)}
      id={SELECTOR.COMMENT_LIST_TOP}
    >
      <CommentListHeader
        isLogin={isLogin}
        onChangeSortType={onChangeSortType}
        sortType={sortType}
        visibleTotalCount={visibleTotalCount}
      />
      {!isLogin ? <LoginRequiredSection /> : null}
      <ul
        className={cn('flex flex-col gap-12 pb-12', {
          'invisible absolute size-0 overflow-hidden': !isLogin,
        })}
      >
        <CommentItems
          commentList={commentList}
          isLogin={isLogin}
          hasPickButton={hasPickButton}
          isInitialChildrenOpened={isInitialChildrenOpened}
          onDeleteSuccess={onDeleteSuccess}
          onLike={handleLike}
          onClickReplyButton={onClickReplyButton}
          onClickEditButton={onClickEditButton}
          onClickPickButton={onClickPickButton}
        />
        {commentListQuery.hasNextPage && isRootComment ? (
          <li ref={ref} className="flex-center">
            <Spinner />
          </li>
        ) : null}
      </ul>
    </section>
  );
};

const CommentCardsPaginated = ({
  isLogin,
  commentType,
  rootCommentId,
  contentId,
  sortType,
  initialPageData,
  onChangeSortType,
  onClickReplyButton,
  onClickEditButton,
  onDeleteSuccess,
  onClickPickButton,
  onChangeCommentData,
  className,
  hasPickButton,
}: CommentCardsProps) => {
  const userId = useAuthStore((state) => state.myInfo?.id);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);

  const { data } = useQuery({
    ...preventSkipToken(
      (isLogin
        ? AuthQueryOptions['get:/wello/v2/auth/wello-comment']
        : UnauthQueryOptions['get:/wello/v2/allows/wello-comment'])({
        query: {
          page: currentPage,
          commentTypeCd: commentType,
          commentSort: sortType,
          size: SIZE,
          parentCommentId: rootCommentId,
          ...toContentIdQuery(contentId),
        },
      }),
    ),
    enabled: !isBot(),
    initialData: currentPage === INITIAL_PAGE ? initialPageData : undefined,
    staleTime: Number.POSITIVE_INFINITY,
    select: ({ context }) => selectCommentList(context, userId),
    placeholderData: keepPreviousData,
  });

  const {
    commentList = [],
    visibleTotalCount = 0,
    totalRootCount = 0,
  } = data ?? {};

  const { like } = useLike();

  const handleLike = (commentId: number, isLike: boolean) => {
    like({
      contentId: commentId,
      isLike,
      likeType: LIKE_TYPE.COMMENT,
    });
  };

  useEffect(() => {
    if (!data) return;
    onChangeCommentData?.(data);
  }, [data, onChangeCommentData]);

  useEffect(() => {
    setCurrentPage(INITIAL_PAGE);
  }, [sortType]);

  const isRootComment = !rootCommentId;
  const isInitialChildrenOpened = commentList.length < 50;

  return (
    <section
      className={cn('bg-Gray-white px-16', className)}
      id={SELECTOR.COMMENT_LIST_TOP}
    >
      <CommentListHeader
        isLogin={isLogin}
        onChangeSortType={onChangeSortType}
        sortType={sortType}
        visibleTotalCount={visibleTotalCount}
      />
      {!isLogin ? <LoginRequiredSection /> : null}
      <ul
        className={cn('flex flex-col gap-12', {
          'invisible absolute size-0 overflow-hidden': !isLogin,
        })}
      >
        <CommentItems
          commentList={commentList}
          isLogin={isLogin}
          hasPickButton={hasPickButton}
          isInitialChildrenOpened={isInitialChildrenOpened}
          onDeleteSuccess={onDeleteSuccess}
          onLike={handleLike}
          onClickReplyButton={onClickReplyButton}
          onClickEditButton={onClickEditButton}
          onClickPickButton={onClickPickButton}
        />
      </ul>
      {isRootComment ? (
        <div className="mb-32 mt-20 flex justify-center pt-8">
          <Pagination
            className="!my-0"
            currentPage={currentPage}
            perButtonCount={5}
            size={SIZE}
            totalCount={totalRootCount}
            onMovePage={setCurrentPage}
          />
        </div>
      ) : null}
    </section>
  );
};

const CommentCards = (props: CommentCardsProps) => {
  if (!isEnumValue(COMMENT_SORT_TYPE, props.sortType))
    throw new CustomError({ return_message: '댓글을 불러올 수 없습니다.' });

  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  return isDesktop ? (
    <CommentCardsPaginated {...props} />
  ) : (
    <CommentCardsInfinite {...props} />
  );
};

export interface CommentListProps extends Omit<CommentCardsProps, 'isLogin'> {
  initialData?: CommentData;
}

export const CommentList = ({
  initialData,
  ...restProps
}: CommentListProps) => {
  const isLogin = useAuthStore((state) => state.isLogin);

  return isLogin !== undefined ? (
    <ErrorBoundary fallback={null}>
      <Suspense fallback={<CommentSkeletons initialData={initialData} />}>
        <CommentCards {...restProps} isLogin={isLogin} />
      </Suspense>
    </ErrorBoundary>
  ) : (
    <CommentSkeletons initialData={initialData} />
  );
};
